'use client';

import { useId, useState } from 'react';
import { ArrowClockwise, ArrowUpRight, CaretDown } from '@phosphor-icons/react';
import { SpinnerIcon } from '@/components/icons';
import { deliveryMoney, type DeliveryTariff } from './delivery-model';
import './delivery-tariff-picker.css';

interface Props {
  tariffs: DeliveryTariff[];
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  freeDelivery: { min_sum: number; max_zone_cost: number };
}

export function DeliveryTariffPicker({
  tariffs,
  selectedKey,
  onSelect,
  loading,
  error,
  onRetry,
  freeDelivery,
}: Props) {
  const titleId = useId();
  const rowsId = useId();
  const [expandedSet, setExpandedSet] = useState<string | null>(null);
  const tariffSet = tariffs.map((tariff) => tariff.key).join('|');
  const expanded = expandedSet === tariffSet;
  const promoApplies =
    freeDelivery.min_sum > 0 &&
    freeDelivery.max_zone_cost > 0 &&
    tariffs.some((tariff) => tariff.cost > 0 && tariff.cost <= freeDelivery.max_zone_cost);

  return (
    <section
      className="delivery-tariff-picker"
      id="delivery-rates"
      aria-labelledby={titleId}
      aria-busy={loading}
    >
      <div className="delivery-tariff-intro">
        <h2 id={titleId}>Тарифы доставки</h2>
        <p>
          Выберите тариф, чтобы увидеть его зоны на карте. Точные условия для вашего дома — после
          проверки адреса.
        </p>
        <button
          className="delivery-tariff-reset"
          type="button"
          onClick={() => onSelect(null)}
          disabled={!selectedKey || loading}
          aria-label="Показать все зоны"
        >
          Все зоны на карте <ArrowUpRight size={17} aria-hidden="true" />
        </button>
      </div>
      <div className="delivery-tariff-table">
        <div className="delivery-rates-head" aria-hidden="true">
          <span>Доставка</span>
          <span>Заказ от</span>
          <span>Ориентир</span>
          <span />
        </div>
        {loading ? (
          <div className="delivery-tariff-state" role="status">
            <SpinnerIcon size={19} className="delivery-spinner" aria-hidden="true" /> Загружаем тарифы…
          </div>
        ) : error ? (
          <div className="delivery-tariff-state is-error" role="status">
            <span>Не удалось загрузить тарифы</span>
            <button type="button" onClick={onRetry}>
              <ArrowClockwise size={16} aria-hidden="true" /> Повторить
            </button>
          </div>
        ) : !tariffs.length ? (
          <div className="delivery-tariff-state" role="status">
            Для этого города тарифов пока нет.
          </div>
        ) : (
          <>
            <div className="delivery-tariffs" id={rowsId} role="group" aria-label="Тарифы доставки">
              {(expanded ? tariffs : tariffs.slice(0, 5)).map((tariff) => {
                const isSelected = selectedKey === tariff.key;
                return (
                  <button
                    type="button"
                    className={`delivery-tariff${isSelected ? ' is-selected' : ''}`}
                    key={tariff.key}
                    onClick={() => onSelect(isSelected ? null : tariff.key)}
                    aria-pressed={isSelected}
                    aria-label={`Доставка ${tariff.cost === 0 ? 'бесплатно' : deliveryMoney(tariff.cost)}, заказ от ${deliveryMoney(tariff.minimum)}, ${tariff.time}. Показать зоны на карте`}
                  >
                    <strong className={tariff.cost === 0 ? 'delivery-tariff-free' : undefined}>
                      {tariff.cost === 0 ? 'Бесплатно' : deliveryMoney(tariff.cost)}
                    </strong>
                    <span>{deliveryMoney(tariff.minimum)}</span>
                    <span>{tariff.time}</span>
                    <ArrowUpRight size={19} aria-hidden="true" />
                  </button>
                );
              })}
            </div>
            {tariffs.length > 5 && (
              <button
                className="delivery-more-rates"
                type="button"
                aria-expanded={expanded}
                aria-controls={rowsId}
                onClick={() => setExpandedSet(expanded ? null : tariffSet)}
              >
                {expanded ? 'Свернуть тарифы' : `Все тарифы · ${tariffs.length}`}{' '}
                <CaretDown size={17} aria-hidden="true" />
              </button>
            )}
            {promoApplies && (
              <p className="delivery-tariff-note">
                При заказе от {deliveryMoney(freeDelivery.min_sum)} доставка бесплатна в зонах со
                стоимостью до {deliveryMoney(freeDelivery.max_zone_cost)}. Остальные зоны — по
                своему тарифу.
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
